// Web Bluetooth driver for the "Shining" LED face masks (BLE name "MASK-xxxxxx").
//
// Protocol (reverse-engineered by the community; see github.com/GoneUp/mask-go and
// github.com/ChrisHaasP3/mask-remote):
// - Service 0xFFF0. Commands go to ...9600, the mask answers on ...9601 (notify),
//   bulk upload data goes to ...960a.
// - A command is [len][ASCII name][args], zero-padded to 16 bytes and AES-128-ECB encrypted
//   with a fixed key shared by every mask. Replies come back encrypted the same way.
// - Uploads (text / pictures): DATS -> mask says DATSOK -> chunks of <=98 bytes on ...960a,
//   each answered with REOK -> DATCP -> mask says DATCPOK.

const SERVICE = 0xfff0;
const CMD_UUID = "d44bc439-abfd-45a2-b575-925416129600";
const NOTIFY_UUID = "d44bc439-abfd-45a2-b575-925416129601";
const UPLOAD_UUID = "d44bc439-abfd-45a2-b575-92541612960a";
const KEY_HEX = "32672f7974ad43451d9c6c894a0e8764";
const CHUNK = 98;

// Counts from one owner's mask; other models may have more or fewer, so the UI also lets you
// type any number. Numbers past the end show garbled pixels rather than an error.
export const IMAGE_COUNT = 69;
export const ANIMATION_COUNT = 45;
export const DIY_SLOTS = 20;

export const MODES = [
  { value: 1, label: "Steady" },
  { value: 2, label: "Blink" },
  { value: 3, label: "Scroll left" },
  { value: 4, label: "Scroll right" },
];

// "M" command effects for text: 0-3 are text gradients, 4-7 background images.
export const TEXT_EFFECTS = [
  { value: -1, label: "None" },
  { value: 0, label: "Gradient 1" },
  { value: 1, label: "Gradient 2" },
  { value: 2, label: "Gradient 3" },
  { value: 3, label: "Gradient 4" },
  { value: 4, label: "X background" },
  { value: 5, label: "Christmas background" },
  { value: 6, label: "Love background" },
  { value: 7, label: "Scream background" },
];

export const isWebBluetoothSupported = () =>
  typeof navigator !== "undefined" && !!navigator.bluetooth;

const hex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

// WebCrypto has no ECB mode. For a single block, AES-CBC with a zero IV gives the ECB result
// in the first 16 bytes. To decrypt one block, append a block that decrypts to valid PKCS#7
// padding so WebCrypto accepts it and strips it.
const ZERO_IV = new Uint8Array(16);
let keyPromise = null;
function aesKey() {
  if (!keyPromise) {
    const raw = new Uint8Array(KEY_HEX.match(/../g).map((h) => parseInt(h, 16)));
    keyPromise = crypto.subtle.importKey("raw", raw, { name: "AES-CBC" }, false, ["encrypt", "decrypt"]);
  }
  return keyPromise;
}
async function ecbEncrypt(block) {
  const out = await crypto.subtle.encrypt({ name: "AES-CBC", iv: ZERO_IV }, await aesKey(), block);
  return new Uint8Array(out).slice(0, 16);
}
async function ecbDecrypt(block) {
  const pad = new Uint8Array(16);
  for (let i = 0; i < 16; i++) pad[i] = 0x10 ^ block[i];
  const buf = new Uint8Array(32);
  buf.set(block);
  buf.set(await ecbEncrypt(pad), 16);
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-CBC", iv: ZERO_IV }, await aesKey(), buf));
}

export class ShiningMask {
  constructor({ onLog = () => {}, onConnectionChange = () => {} } = {}) {
    this.onLog = onLog;
    this.onConnectionChange = onConnectionChange;
    this.device = null;
    this.cmdChar = null;
    this.uploadChar = null;
    this.connected = false;
    this.manualDisconnect = false;
    this.queue = Promise.resolve();
    this.waiters = [];
    this.handleNotify = this.handleNotify.bind(this);
    this.handleDisconnect = this.handleDisconnect.bind(this);
  }

  log(msg) {
    this.onLog(msg);
  }

  setConnected(on) {
    this.connected = on;
    this.onConnectionChange(on, this.device?.name || null);
  }

  // Must be called from a user gesture (click): the browser shows its device picker.
  async connect({ showAll = false } = {}) {
    if (!isWebBluetoothSupported()) {
      throw new Error("Web Bluetooth isn't available in this browser. Use Chrome or Edge (not iPhone).");
    }
    if (!this.device || showAll) {
      const opts = showAll
        ? { acceptAllDevices: true, optionalServices: [SERVICE] }
        : { filters: [{ namePrefix: "MASK" }, { services: [SERVICE] }], optionalServices: [SERVICE] };
      this.device = await navigator.bluetooth.requestDevice(opts);
      this.device.addEventListener("gattserverdisconnected", this.handleDisconnect);
      this.log(`picked ${this.device.name || "(no name)"}`);
    }
    await this.gattConnect();
  }

  async gattConnect() {
    this.log("connecting…");
    const server = await this.device.gatt.connect();
    const svc = await server.getPrimaryService(SERVICE);
    this.cmdChar = await svc.getCharacteristic(CMD_UUID);
    this.uploadChar = await svc.getCharacteristic(UPLOAD_UUID).catch(() => null);
    try {
      const notifyChar = await svc.getCharacteristic(NOTIFY_UUID);
      notifyChar.addEventListener("characteristicvaluechanged", this.handleNotify);
      await notifyChar.startNotifications();
    } catch (e) {
      this.log(`notifications unavailable: ${e.message}`);
    }
    this.manualDisconnect = false;
    this.setConnected(true);
    this.log(`connected${this.uploadChar ? "" : " (no upload characteristic: text/picture upload won't work)"}`);
  }

  disconnect() {
    this.manualDisconnect = true;
    if (this.device?.gatt?.connected) this.device.gatt.disconnect();
    else this.setConnected(false);
  }

  async handleDisconnect() {
    this.setConnected(false);
    if (this.manualDisconnect) {
      this.log("disconnected");
      return;
    }
    this.log("mask disconnected, retrying…");
    for (let i = 1; i <= 3; i++) {
      await new Promise((r) => setTimeout(r, 1000 * i));
      try {
        await this.gattConnect();
        return;
      } catch (e) {
        this.log(`retry ${i} failed: ${e.message}`);
      }
    }
    this.log("gave up. Click Connect again.");
  }

  // GATT allows one operation at a time; everything goes through this queue.
  enqueue(fn) {
    const run = this.queue.then(fn);
    this.queue = run.catch(() => {});
    return run;
  }

  async write(ch, data, preferResponse) {
    const p = ch.properties;
    if (preferResponse && p.write) return ch.writeValueWithResponse(data);
    if (p.writeWithoutResponse) return ch.writeValueWithoutResponse(data);
    return ch.writeValueWithResponse(data);
  }

  async sendNow(name, args = []) {
    if (!this.connected) throw new Error("not connected");
    const body = [...new TextEncoder().encode(name), ...args];
    if (body.length > 15) throw new Error(`command ${name} too long`);
    const plain = new Uint8Array(16);
    plain[0] = body.length;
    plain.set(body, 1);
    await this.write(this.cmdChar, await ecbEncrypt(plain), true);
    this.log(`→ ${name} ${args.join(",")}`.trim());
  }

  command(name, args = []) {
    return this.enqueue(() => this.sendNow(name, args));
  }

  waitFor(prefix, ms) {
    return new Promise((resolve, reject) => {
      const w = { prefix, resolve };
      this.waiters.push(w);
      setTimeout(() => {
        if (this.waiters.includes(w)) {
          this.waiters = this.waiters.filter((x) => x !== w);
          reject(new Error(`timeout waiting for ${prefix}`));
        }
      }, ms);
    });
  }

  async handleNotify(e) {
    const v = e.target.value;
    const raw = new Uint8Array(v.buffer, v.byteOffset, v.byteLength).slice();
    let text = "";
    let plain = raw;
    try {
      if (raw.length >= 16) {
        plain = await ecbDecrypt(raw.slice(0, 16));
        text = new TextDecoder().decode(plain.slice(1, 1 + Math.min(plain[0], 15)));
      }
    } catch {
      // not an encrypted reply
    }
    if (!/^[A-Z]{3,}/.test(text)) text = new TextDecoder().decode(raw.slice(1)).replace(/[^\x20-\x7e]/g, "");
    this.log(`← ${text.replace(/[^\x20-\x7e]/g, "·") || "?"} [${hex(plain)}]`);
    const hit = this.waiters.find((w) => text.startsWith(w.prefix));
    if (hit) {
      this.waiters = this.waiters.filter((x) => x !== hit);
      hit.resolve(text);
    }
  }

  // ---- simple commands ----
  setBrightness(value) { return this.command("LIGHT", [clampByte(value)]); }
  setSpeed(value) { return this.command("SPEED", [clampByte(value)]); }
  setMode(mode) { return this.command("MODE", [mode]); }
  // The mask numbers built-ins from 0; the UI shows them from 1.
  showImage(n) { return this.command("IMAG", [n - 1]); }
  showAnimation(n) { return this.command("ANIM", [n - 1]); }
  playDiy(slot) { return this.command("PLAY", [1, slot]); }
  setTextColor(rgb) { return this.command("FC", rgb ? [1, ...rgb] : [0, 0, 0, 0]); }
  setBackgroundColor(rgb) { return this.command("BC", rgb ? [1, ...rgb] : [0, 0, 0, 0]); }
  setTextEffect(effect) { return this.command("M", effect < 0 ? [0, 0] : [1, effect]); }

  // ---- uploads ----
  async uploadNow(data, datsArgs) {
    if (!this.uploadChar) throw new Error("this mask has no upload characteristic");
    const ok = this.waitFor("DATS", 3000);
    await this.sendNow("DATS", datsArgs);
    await ok.catch(() => this.log("no DATSOK, continuing anyway"));

    let acks = true;
    for (let off = 0, n = 0; off < data.length; off += CHUNK, n++) {
      const chunk = data.slice(off, off + CHUNK);
      const pkt = new Uint8Array(chunk.length + 2);
      pkt[0] = chunk.length + 1;
      pkt[1] = n & 255;
      pkt.set(chunk, 2);
      const ack = acks ? this.waitFor("REOK", 1500) : null;
      await this.write(this.uploadChar, pkt, false);
      if (acks) {
        const acked = await ack.then(() => true, () => false);
        if (!acked) {
          acks = false;
          this.log("no REOK acks, switching to timed pacing");
        }
      } else {
        await new Promise((r) => setTimeout(r, 100));
      }
    }

    const t = Math.floor(Date.now() / 1000);
    const done = this.waitFor("DATCP", 3000);
    await this.sendNow("DATCP", [(t >>> 24) & 255, (t >>> 16) & 255, (t >>> 8) & 255, t & 255]);
    await done.catch(() => this.log("no DATCPOK (it may still have worked)"));
  }

  // columns: array of 16-element boolean arrays (top row first); colors: [r,g,b] per column.
  uploadText(columns, colors, { mode = 3, speed = 50, textColor = null, effect = -1 } = {}) {
    const w = columns.length;
    const bitmap = new Uint8Array(w * 2);
    const colorBytes = new Uint8Array(w * 3);
    columns.forEach((col, x) => {
      let a = 0;
      let b = 0;
      for (let y = 0; y < 8; y++) if (col[y]) a |= 0x80 >> y;
      for (let y = 8; y < 16; y++) if (col[y]) b |= 0x80 >> (y - 8);
      bitmap[2 * x] = a;
      bitmap[2 * x + 1] = b;
      colorBytes.set(colors[x], 3 * x);
    });
    const data = new Uint8Array(bitmap.length + colorBytes.length);
    data.set(bitmap);
    data.set(colorBytes, bitmap.length);
    const total = data.length;
    const bl = bitmap.length;
    return this.enqueue(async () => {
      this.log(`uploading text: ${w} columns, ${total} bytes`);
      await this.uploadNow(data, [total >> 8, total & 255, bl >> 8, bl & 255, 0]);
      await this.sendNow("MODE", [mode]);
      await this.sendNow("SPEED", [clampByte(speed)]);
      await this.sendNow("M", effect < 0 ? [0, 0] : [1, effect]);
      // Per-column colors only show when the front-color override is off.
      await this.sendNow("FC", textColor ? [1, ...textColor] : [0, 0, 0, 0]);
      this.log("text sent");
    });
  }

  // rgb: width*height*3 bytes, column-major from the top-left. Stored in a DIY slot, then played.
  uploadPicture(rgb, slot) {
    return this.enqueue(async () => {
      this.log(`uploading picture to slot ${slot}: ${rgb.length} bytes`);
      await this.uploadNow(rgb, [rgb.length >> 8, rgb.length & 255, slot >> 8, slot & 255, 1]);
      await this.sendNow("PLAY", [1, slot]);
      this.log(`picture sent to slot ${slot}`);
    });
  }
}

function clampByte(v) {
  return Math.max(0, Math.min(255, Math.round(+v || 0)));
}

export function hexToRgb(h) {
  const n = parseInt(h.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rainbow(x) {
  const h = (x * 9) % 360;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (0.5 - 0.5 * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };
  return [f(0), f(8), f(4)];
}

// Renders text into 16-pixel-tall columns for uploadText().
export function renderTextColumns(text, { fontSize = 14, bold = true } = {}) {
  const font = `${bold ? "bold " : ""}${fontSize}px system-ui, sans-serif`;
  const measure = document.createElement("canvas").getContext("2d");
  measure.font = font;
  const w = Math.max(1, Math.ceil(measure.measureText(text || " ").width) + 2);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = 16;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, 16);
  ctx.fillStyle = "#fff";
  ctx.font = font;
  ctx.textBaseline = "middle";
  ctx.fillText(text || " ", 1, 8.5);
  const d = ctx.getImageData(0, 0, w, 16).data;
  const columns = [];
  for (let x = 0; x < w; x++) {
    const col = [];
    for (let y = 0; y < 16; y++) col.push(d[(y * w + x) * 4] > 110);
    columns.push(col);
  }
  return columns;
}

// Reads a canvas into the column-major RGB byte layout uploadPicture() expects.
export function canvasToColumnRgb(canvas) {
  const { width, height } = canvas;
  const d = canvas.getContext("2d").getImageData(0, 0, width, height).data;
  const out = new Uint8Array(width * height * 3);
  let i = 0;
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      const p = (y * width + x) * 4;
      out[i++] = d[p];
      out[i++] = d[p + 1];
      out[i++] = d[p + 2];
    }
  }
  return out;
}
